import {  Plus, ArrowBigLeft, ArrowBigRight } from "lucide-react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Popup } from "@/components/common/Popup";
import { Booking } from "@/components/booking/Booking";
import { BookingInfo } from "@/components/booking/BookingInfo";
import type { BookingInfoSchema } from "@/components/booking/BookingSchema";
import type { OrderInfoSchema } from "@/components/order/OrderSchema";
import React, { useState, useEffect } from "react";
import { Dropdown } from "@/components/ui/select";
import { getCalendarWeek, setMonthDays } from '@/helper/sort';
import { useApiFetch } from "@/helper/apiFetch";
import { useAuth } from "@/auth";
import { useTranslate } from "@/helper/translate";


type Lookup = { id: string; name: string, loopLength?: number };

export function BookingPage() {
  const apiFetch = useApiFetch();
  const { linkedCustomerId } = useAuth();
  const { translate, formatMonthYear } = useTranslate();
  const [isLoaded, setIsLoaded] = useState(false);

  const [editBooking, setEditBooking] = useState<BookingInfoSchema | null>(null);
  const daysOfWeek = ["KW", "Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"];
  const now = new Date();
  const initialDate = new Date(now.getFullYear(), now.getMonth(), 1);
  const [currentDate, setCurrentDate] = useState(initialDate);
  const [days, setDays] = useState<Date[]>([]);
  const [orders, setOrders] = useState<OrderInfoSchema[]>();
  const [schedule, setBookings] = useState<BookingInfoSchema[]>([]);
  const [selectedSite, setSelectedSite] = useState<string>("");
  const [selectedCustomer, setSelectedCustomer] = useState<string>("");
  const [customers, setCustomers] = useState<Lookup[]>([]);
  const [sites, setSites] = useState<Lookup[]>([]);

  const handleCustomerChange = (customerId: string) => {
    setSites([]);
    setSelectedCustomer(customerId);

  }
  const handleSiteChange = (siteId: string) => {
    setOrders([]);
    setBookings([]);
    setSelectedSite(siteId);
  }

  

  const backward = () => {
    setOrders([]);
    setBookings([]);
    const newDate = new Date(currentDate);
    newDate.setMonth(currentDate.getMonth() - 1);
    setCurrentDate(newDate);
    setDays(setMonthDays(newDate));
  };

  const forward = () => {
    setOrders([]);
    setBookings([]);
    const newDate = new Date(currentDate);
    newDate.setMonth(currentDate.getMonth() + 1);
    setCurrentDate(newDate);
    setDays(setMonthDays(newDate));
  };



  const addBooking = () => {
    const booking: BookingInfoSchema = {
      bookingId: null,
      spotId: "",
      spotName: "",
      bookingBegin:  new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0),
      bookingEnd: null,
      orderId: "",
      statusId: 1,
      length: 0
    };
    setEditBooking(booking);
  };

  const updateBooking = (booking: BookingInfoSchema | null) => {
    if (booking) {

      if (editBooking?.bookingId == null)
        setBookings(prev => [...prev, booking]);
      else
        setBookings(prev =>
          prev.map(c => (c.bookingId === booking.bookingId ? booking : c))
        );
    }
    setEditBooking(null);
  };

  const rows = [];
  for (let i = 0; i < days.length; i += 7) {
    const weekDays = days.slice(i, i + 7);
    const weekNumber = getCalendarWeek(weekDays[0]);
    rows.push({ weekNumber, days: weekDays });
  }

  const orderDays = (order: OrderInfoSchema): Date[] => {
    const start = new Date(order.orderBegin);
    const end = new Date(order.orderEnd);

    // normalize to UTC midnight to avoid DST issues
    let d = start;// new Date(Date.UTC(start.getUTCFullYear(), start.getUTCMonth(), start.getUTCDate()));
    const endUtc = end;// new Date(Date.UTC(end.getUTCFullYear(), end.getUTCMonth(), end.getUTCDate()));

    const out: Date[] = [];
    while (d <= endUtc) {
      out.push(new Date(d));                 // clone
      //d.setUTCDate(d.getUTCDate() + 1);      // next day
      d.setDate(d.getDate() + 1);      // next day
    }
    return out;
  };

   useEffect(() => {
    if (linkedCustomerId != "Admin") {
      setCustomers([
        { id: linkedCustomerId, name: "" }
      ]);
      setSelectedCustomer(linkedCustomerId);

    }
    else {
      apiFetch(`/App/GetCustomerLookup?all=false`)//only customers with active orders
        .then((res) => {
          if (!res.ok) throw new Error("Failed to fetch customers");
          return res.json();
        })
        .then((data) => {
          setCustomers(data);
          if (data.length > 0) {
            setSelectedCustomer(data[0].id);
          }
        })
        .catch((err) => {
          console.error(err);
        });
    }
  }, [linkedCustomerId]);



  useEffect(() => {
    {
      if (selectedCustomer && selectedCustomer != "") {
    setOrders([]);
        setBookings([]);

        apiFetch(`/App/GetSiteLookup?customerId=${selectedCustomer}`) //only sites with active orders
          .then((res) => {
            if (!res.ok) throw new Error("Failed to fetch containers");
            return res.json();
          })
          .then((data) => {
            setSites(data);
            setSelectedSite(data[0].id);


          })
          .catch((err) => {
            console.error(err);
          });

      }
    }
  }, [selectedCustomer]);


  useEffect(() => {
    {

      if (selectedSite && selectedSite != "" && selectedCustomer && selectedCustomer != "") {
          setIsLoaded(false);

        apiFetch(`/App/GetBookingSchedule?siteId=${selectedSite}&customerId=${selectedCustomer}&date=${currentDate.toISOString()}`)
          .then((res) => {
            if (!res.ok) throw new Error("Failed to fetch playlist");
            return res.json();
          })
          .then((data) => {
            setOrders(data.orders);
            setBookings(data.bookings);
            setDays(setMonthDays(currentDate));
            setIsLoaded(true);

          })
          .catch((err) => {
            console.error(err);
          });

      }
    }
  }, [selectedCustomer, selectedSite, currentDate]);


  return (
    <div className="h-[calc(100vh-60px)] flex flex-col">
      <div className="content-header flex justify-between pt-[0.6rem] pb-[0.6rem] px-8 bg-gray-100 dark:bg-zinc-900">
        <div className="flex items-center gap-4">
          <h1 className="font-bold text-2xl">  {translate("ui.bookings")} </h1>

          {linkedCustomerId == "Admin" && ( 
            <Dropdown
            className="w-[20rem]"
            source={customers}
            value={selectedCustomer}
            valueMember={"id"}
            displayMember={"name"}
            onChange={(value) => handleCustomerChange(value as string)}
          />)}
         

          <Dropdown
            className="w-[20rem]"
            source={sites}
            value={selectedSite}
            valueMember={"id"}
            displayMember={"name"}
            onChange={(value) => handleSiteChange(value as string)}
          />


        </div>
      </div>





      <Popup
        isOpen={editBooking !== null}
        onClose={() => setEditBooking(null)}
        title=  {translate("ui.bookingInfo")} 
        className="w-[800px]"
      >
        <BookingInfo onClose={(booking) => { updateBooking(booking ?? null); }} onDeleted={() => {setEditBooking(null); setBookings(prev => prev.filter(c => c.bookingId !== editBooking?.bookingId)); }} booking={editBooking as BookingInfoSchema} customerId={selectedCustomer} siteId={selectedSite}  />
      </Popup>

{!isLoaded ? (
        <div className="w-full text-center my-10">{translate("common.loading")}</div>
      ) : (
      <div className="flex-1 overflow-auto px-10">
        <div className="overflow-x pb-5 mt-3">
          <div className="calendarWrapper bookingsCalendar">
            <div className="calendarHeader justify-center select-none">
              <div className="cursor-pointer flex absolute left-0 text-sky-600 hover:text-sky-700 dark:text-orange-400 dark:hover:text-orange-500 items-center" onClick={addBooking}>
                <Plus className="text-white dark:text-black bg-sky-600 hover:bg-sky-700 dark:bg-orange-400 dark:hover:bg-orange-500 rounded-full mr-2" />   {translate("ui.book")} 
              </div>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                <div className="calendarNav left text-sky-600 dark:text-orange-400" onClick={backward}><ChevronLeft /></div>
                <div className="w-[280px] uppercase tracking-[2px] font-light select-none" style={{ textAlign: "center", fontSize: "20px", cursor: "pointer" }}>{formatMonthYear(currentDate)}</div>
                <div className="calendarNav right text-sky-600 dark:text-orange-400" onClick={forward}><ChevronRight /></div>
              </div>
            </div>
            {true ? (
              <div className="overflow-x-auto mt-2 mb-10 rounded-xl shadow-2xl">
                <table className="w-full calendarTable border-[0] border-collapse select-none">
                <colgroup>
                  <col style={{ width: "16%" }} />
                  {Array.from({ length: 7 }).map((_, idx) => (
                    <col key={idx} style={{ width: "12%" }} />
                  ))}
                </colgroup>
                <thead>
                  <tr>
                    {daysOfWeek.map((day, idx) => (
                      <td key={idx} className="calendarItem itemHeader bg-gray-200 text-sm text-gray-600 dark:bg-zinc-800 dark:text-zinc-200">{day}</td>
                    ))}
                  </tr>
                </thead>
                <tbody className="text-[0.8rem]">
                  {rows.map((row, rIdx) => (
                    <React.Fragment key={rIdx}>
                      <tr>
                        <td className="calendarItem calendarKW bookings bg-gray-200 text-center font-bold text-base dark:bg-zinc-800 dark:text-zinc-200">{row.weekNumber}</td>
                        {row.days.map((day, dIdx) => (
                          <td
                            key={dIdx}
                            className={day.toDateString() === new Date().toDateString()
                              ? "calendarItem today calendarDay  dark:bg-zinc-700"
                              : "calendarItem calendarDay  dark:bg-zinc-700"}
                          >
                            <div className="calendarDayHeader flex justify-end">
                              <div className="flex items-center justify-center text-base font-semibold text-gray-700 dark:text-zinc-200 w-[24px] h-[24px] rounded-full bg-transparent">{day.getDate()}</div>
                            </div>
                          </td>
                        ))}
                      </tr>
                      {orders?.filter(order =>
                        new Date(order.orderBegin) <= row.days[row.days.length - 1] &&
                        new Date(order.orderEnd) >= row.days[0]
                      ).flatMap(order =>
                        Array.from({ length: order.numberOfLoops }).flatMap((_, loop) => (

                          <tr key={`${order.orderId}-${loop}`} data-info={`${order.orderId}-${loop}`}>
                            <td className="calendarItem calendarKW bookings bg-gray-100 dark:bg-zinc-700 text-center">
                              <div> {order.orderName} / {translate("ui." + loop)}</div>
                            </td>
                            {row.days.map((day, dIdx) => (
                              <Booking
                                key={`${order.orderId}-${loop}-${dIdx}`}
                                order={order}
                                schedule={schedule.filter(s => s.orderId === order.orderId)}
                                loop={loop}
                                day={day}
                                onClick={(booking) => { console.log(booking); setEditBooking(booking); }}
                              />
                            ))}
                          </tr>
                        )
                        )
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
                </table>
              </div>
            ) : (

              orders?.map((order, dIdx) => {
                const days = orderDays(order);
                return (
                  <div className="orderWrapper" key={order.orderId}>
                    <div className="order">{order.orderName} - {new Date(order.orderBegin).toLocaleDateString()} - {new Date(order.orderEnd).toLocaleDateString()}
                    </div>
                    <table className="orderLoop">

                      <tbody>
                        {Array.from({ length: order.numberOfLoops }).map((_, loop) => (


                          <tr key={`${order.orderId}-${loop}`} className="loop">
                            <th className="loopHeader" scope="row">  {translate("ui.loop")}  {loop + 1}</th>

                            {days.map((day) => (
                              <Booking
                                key={`${order.orderId}-${loop}-${dIdx}`}
                                order={order}
                                schedule={schedule.filter(s => s.orderId === order.orderId)}
                                loop={loop - 1}
                                day={day}
                                onClick={(booking) => { console.log(booking); setEditBooking(booking); }}
                              />
                            ))}
                          </tr>

                        ))}
                      </tbody>



                    </table>
                  </div>

                )
              }))
            }
          </div>
        </div>
      </div>)}
    </div>
  );
}
